import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sparkles, TrendingUp, AlertTriangle, CheckCircle, RefreshCcw, Quote } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { normalizeAIInsights } from '@/lib/aiInsightsNormalizer';

interface AIInsightsProps {
  insights: unknown;
  onRefresh: () => void;
  isRefreshing: boolean;
  formTitle: string;
}

export const AIInsightsCard = ({ insights, onRefresh, isRefreshing, formTitle }: AIInsightsProps) => {
  if (!insights) {
    return (
      <Card className="card-elegant border-amber-500/20 bg-amber-500/5">
        <CardContent className="pt-6 text-center">
          <Sparkles className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold mb-2">No AI Summary Yet</h3>
          <p className="text-muted-foreground text-sm max-w-sm mx-auto mb-4">
            Collect more responses or click the button below to generate an AI-powered summary of your response data for {formTitle || 'Selected Form'}.
          </p>
          <Button 
            onClick={onRefresh} 
            disabled={isRefreshing}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {isRefreshing ? (
              <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Generate AI Summary
          </Button>
        </CardContent>
      </Card>
    );
  }

  const normalized = normalizeAIInsights(insights);
  const sentiment = normalized.customer_sentiment_breakdown;

  return (
    <div className="space-y-6 animate-slide-up">
      <Card className="card-elegant border-amber-500/20 shadow-glow-amber">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-500" />
              <CardTitle className="text-xl">AI-Generated Insights</CardTitle>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onRefresh} 
              disabled={isRefreshing}
              className="text-amber-600 hover:text-amber-700 hover:bg-amber-500/10"
            >
              <RefreshCcw className={isRefreshing ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
            </Button>
          </div>
          <CardDescription className="flex items-center gap-1">
            Synthesized from your recent form submissions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Summary Box */}
          <div className="bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent p-5 rounded-xl border border-amber-500/10 relative overflow-hidden">
            <Quote className="absolute top-2 right-4 h-12 w-12 text-amber-500/10 rotate-180" />
            <p className="text-foreground leading-relaxed italic text-lg pr-8">
              "{normalized.summary}"
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sentiment Breakdown */}
            <div className="space-y-4">
              <h4 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                <TrendingUp className="h-4 w-4" />
                Response Sentiment
              </h4>
              <div className="space-y-3 p-4 bg-muted/30 rounded-xl">
                <div className="space-y-1">
                  <div className="flex justify-between text-xs font-medium">
                    <span className="text-success">Positive</span>
                    <span>{sentiment.positive}%</span>
                  </div>
                  <Progress value={sentiment.positive} className="h-1.5 bg-success/10 [&>div]:bg-success" />
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs font-medium">
                    <span className="text-muted-foreground">Neutral</span>
                    <span>{sentiment.neutral}%</span>
                  </div>
                  <Progress value={sentiment.neutral} className="h-1.5 bg-muted [&>div]:bg-muted-foreground" />
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs font-medium">
                    <span className="text-destructive">Negative</span>
                    <span>{sentiment.negative}%</span>
                  </div>
                  <Progress value={sentiment.negative} className="h-1.5 bg-destructive/10 [&>div]:bg-destructive" />
                </div>
              </div>
            </div>

            {/* Top Requests */}
            <div className="space-y-4">
              <h4 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                <CheckCircle className="h-4 w-4" />
                Top Requests
              </h4>
              <div className="flex flex-wrap gap-2">
                {normalized.top_requests.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No top requests identified.</p>
                ) : (
                  normalized.top_requests.map((request, i) => (
                    <Badge key={i} variant="secondary" className="bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 border-amber-500/20 px-3 py-1">
                      {request}
                    </Badge>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-border/50">
            {/* Key Insights */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Key Observations</h4>
              <div className="space-y-3">
                {normalized.key_insights.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No observations available.</p>
                ) : (
                  normalized.key_insights.map((item, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold leading-none mb-1">{item.title}</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Recommended Actions */}
            <div className="space-y-4">
              <h4 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Recommended Actions
              </h4>
              <div className="space-y-2">
                {normalized.recommended_actions.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No actions recommended.</p>
                ) : (
                  normalized.recommended_actions.map((action, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                      <span className="text-muted-foreground">{action}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
